import { useState, useRef, useCallback } from 'react';
import { calculateSdaIncomeAndNoi } from '../utils/sdaFormulaCalculator';
import { parseDealDocument } from '../services/documentParsingService';
import '../styles/quickDealAnalysis.css';
import '../styles/extractionLog.css';

// ─── Session persistence ──────────────────────────────────────────────────────
// File objects (binary) can't go into localStorage, so we save:
//   - analysisResult  → the full analysis JSON
//   - fileNames       → { om, rentRoll, t12, photos } name strings for display
//   - parsedData      → the merged parsed financials (so Re-Run works without re-uploading)
//   - listingLink     → the URL
const QDA_SESSION_KEY = 'qda_session';

function saveQdaSession({ analysisResult, fileNames, parsedData, listingLink }) {
  try {
    localStorage.setItem(QDA_SESSION_KEY, JSON.stringify({
      analysisResult,
      fileNames,
      parsedData,
      listingLink,
      savedAt: Date.now(),
    }));
  } catch { }
}

function loadQdaSession() {
  try {
    const raw = localStorage.getItem(QDA_SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function clearQdaSession() {
  try { localStorage.removeItem(QDA_SESSION_KEY); } catch { }
}

// ─── Document slot definitions ────────────────────────────────────────────────
const DOC_TYPES = [
  { id: 'om', label: 'OM', sublabel: 'Offering Memo', icon: '📄', accept: '.pdf,.txt', color: '#0f2d5e', bg: '#eff6ff' },
  { id: 'rentRoll', label: 'Rent Roll', sublabel: 'XLSX / PDF', icon: '📊', accept: '.xlsx,.xls,.pdf', color: '#1d4ed8', bg: '#eff6ff' },
  { id: 't12', label: 'T12 / P&L', sublabel: 'Financials', icon: '📈', accept: '.xlsx,.xls,.pdf', color: '#2563eb', bg: '#eff6ff' },
  { id: 'photos', label: 'Photos', sublabel: 'JPG / PNG', icon: '🖼️', accept: '.jpg,.jpeg,.png', color: '#1e3a8a', bg: '#eff6ff' },
  { id: 'link', label: 'Link', sublabel: 'Listing URL', icon: '🔗', accept: null, color: '#2563eb', bg: '#eff6ff' },
];

// ─── Helper: format currency ──────────────────────────────────────────────────
const fmt = (n) => {
  if (!n && n !== 0) return '—';
  const num = Number(n);
  if (!isFinite(num)) return '—';
  if (Math.abs(num) >= 1_000_000) return `$${(num / 1_000_000).toFixed(2)}M`;
  if (Math.abs(num) >= 1_000) return `$${Math.round(num).toLocaleString()}`;
  return `$${num.toFixed(0)}`;
};

const fmtPct = (n) => {
  const num = Number(n);
  if (!isFinite(num)) return '—';
  return `${num.toFixed(1)}%`;
};

// ─── Core analysis engine ─────────────────────────────────────────────────────
function runAnalysis({ parsed, listingLink, uploadedSources }) {
  // Merge all parsed data sources
  const p = parsed || {};
  const financials = p.financials || {};
  const property = p.property || {};
  const rentRollData = p.rentRoll || {};

  // Pull values with fallbacks
  const units = Number(property.units || financials.units || 0);
  const price = Number(property.askingPrice || financials.askingPrice || 0);
  const occupancy = Number(property.occupancy || rentRollData.occupancyRate || 0);
  // Prefer the parsed economic vacancy rate (from T12/OM income deduction lines)
  // over the physical occupancy-derived vacancy, which excludes bad debt & concessions.
  const parsedVacancyRate = Number(financials.vacancyRate || 0);
  const vacancyRate = parsedVacancyRate > 0
    ? parsedVacancyRate
    : (occupancy ? (100 - occupancy) : 5);

  const grossRentMonthly = Number(financials.grossRent || 0);
  const grossRentAnnual = Number(financials.grossPotentialRent || grossRentMonthly * 12 || 0);
  // otherIncome is stored as monthly by all parsers — multiply by 12 for annual use
  const otherIncomeAnnual = Number(financials.otherIncome || 0) * 12;
  const opExpenses = Number(financials.opExpenses || financials.totalOperatingExpenses || 0);
  const statedNOI = Number(financials.noi || 0);
  const capRateStated = Number(financials.capRate || 0);

  // USA CRE standard: always trust T12 NOI as the baseline.
  // trustParsedNoi=true means the SDA formula uses the document NOI directly
  // instead of recalculating from gross - vacancy - expenses (which can drift
  // when vacancy rate or expenses were extracted from different sources).
  const hasTrustedDocData = statedNOI > 1000 && opExpenses > 1000;

  const calc = calculateSdaIncomeAndNoi({
    grossPotentialRentAnnual: grossRentAnnual,
    grossRentMonthly,
    otherIncomeAnnual,
    vacancyRate,
    operatingExpensesAnnual: opExpenses,
    parsedNoi: statedNOI,
    trustParsedNoi: statedNOI > 1000,   // trust T12/OM extracted NOI
  });

  const computedNOI = hasTrustedDocData ? statedNOI : calc.noi;
  const totalRevenue = hasTrustedDocData
    ? statedNOI + opExpenses                         // Revenue = NOI + Expenses (from doc)
    : (calc.effectiveGrossIncome || grossRentAnnual);
  const totalExpenses = calc.totalOperatingExpenses || opExpenses;
  const expenseRatio = totalRevenue > 0 ? (totalExpenses / totalRevenue) * 100 : 0;
  const computedCapRate = price > 0 && computedNOI > 0 ? (computedNOI / price) * 100 : 0;
  const displayNOI = computedNOI > 0 ? computedNOI : statedNOI;
  const displayCapRate = computedCapRate > 0 ? computedCapRate : capRateStated;

  // ── NOI Mismatch check ────────────────────────────────────────────────
  // Guard against PDF parsing artifacts (e.g. a page number "78" extracted as NOI).
  // statedNOI must be > $1,000 AND > 5% of computedNOI to be treated as a real broker figure.
  const noiIsPlausible = statedNOI > 1000 && (computedNOI <= 0 || statedNOI / computedNOI > 0.05);
  const noiMismatch = noiIsPlausible && computedNOI > 0 &&
    Math.abs(statedNOI - computedNOI) / Math.max(statedNOI, computedNOI) > 0.15;

  // ── Expense analysis ──────────────────────────────────────────────────
  let expenseVerdict = '';
  let expenseSeverity = 'neutral';
  if (totalExpenses === 0 && totalRevenue === 0) {
    expenseVerdict = 'No financial data provided. Expense analysis cannot be completed without T12/P&L or OM financials.';
    expenseSeverity = 'warning';
  } else if (totalExpenses === 0) {
    expenseVerdict = 'Operating expenses not provided. The stated NOI cannot be independently verified. Request the T12/P&L to confirm.';
    expenseSeverity = 'warning';
  } else if (expenseRatio < 35) {
    expenseVerdict = `Expense ratio of ${fmtPct(expenseRatio)} is unusually low. This is a red flag — expenses may be understated or key costs (taxes, insurance, management) are missing from the pro forma.`;
    expenseSeverity = 'danger';
  } else if (expenseRatio <= 55) {
    expenseVerdict = `Expense ratio of ${fmtPct(expenseRatio)} is within normal multifamily range (40–55%). Expenses appear reasonable. Verify with full T12 if not already provided.`;
    expenseSeverity = 'good';
  } else if (expenseRatio <= 65) {
    expenseVerdict = `Expense ratio of ${fmtPct(expenseRatio)} is elevated. Operating costs are consuming a high share of revenue. Dig into which expense categories are driving this — management fees, deferred maintenance, or taxes may be outsized.`;
    expenseSeverity = 'warning';
  } else if (expenseRatio <= 70) {
    expenseVerdict = `Expense ratio of ${fmtPct(expenseRatio)} is high. More than 65% of revenue is going to operating expenses. NOI is under pressure — review expense line items and identify reduction opportunities before committing.`;
    expenseSeverity = 'warning';
  } else {
    expenseVerdict = `Expense ratio of ${fmtPct(expenseRatio)} is critically high. More than 70% of revenue is consumed by operating expenses. The deal economics are severely compressed.`;
    expenseSeverity = 'danger';
  }

  // ── Occupancy analysis ────────────────────────────────────────────────
  let occupancyNote = '';
  if (occupancy === 0) {
    occupancyNote = 'Occupancy not provided.';
  } else if (occupancy >= 93) {
    occupancyNote = `Occupancy at ${fmtPct(occupancy)} is strong.`;
  } else if (occupancy >= 85) {
    occupancyNote = `Occupancy at ${fmtPct(occupancy)} is acceptable but below market. Investigate tenant turnover and lease terms.`;
  } else {
    occupancyNote = `Occupancy at ${fmtPct(occupancy)} is low. This is a significant risk. Verify lease expirations and reason for vacancy.`;
  }

  // ── Key Findings ─────────────────────────────────────────────────────
  const positives = [];
  const concerns = [];

  if (displayNOI > 0) positives.push(`Positive NOI of ${fmt(displayNOI)}`);
  if (occupancy >= 90) positives.push(`Strong occupancy at ${fmtPct(occupancy)}`);
  if (displayCapRate >= 7) positives.push(`Cap rate of ${fmtPct(displayCapRate)} is above market average`);
  if (expenseRatio > 0 && expenseRatio <= 55) positives.push('Expense ratio is within normal range');
  if (price > 0 && units > 0 && price / units < 80000) positives.push(`Low per-unit price of ${fmt(price / units)}`);

  if (noiMismatch) concerns.push(`Stated NOI (${fmt(statedNOI)}) differs from computed NOI (${fmt(computedNOI)}) — cross-check required`);
  if (occupancy > 0 && occupancy < 85) concerns.push(`Low occupancy (${fmtPct(occupancy)}) signals lease-up risk`);
  if (displayCapRate > 0 && displayCapRate < 5.5) concerns.push(`Cap rate of ${fmtPct(displayCapRate)} is below typical acquisition threshold`);
  if (expenseRatio > 55) concerns.push(`High expense ratio (${fmtPct(expenseRatio)}) compresses NOI`);
  if (expenseRatio > 0 && expenseRatio < 35) concerns.push('Suspiciously low expense ratio — verify all line items');
  if (!uploadedSources.includes('T12 / P&L')) concerns.push('T12 not provided — actuals unverified');
  if (!uploadedSources.includes('Rent Roll')) concerns.push('Rent roll not provided — tenant income unverified');

  // ── Red Flags ─────────────────────────────────────────────────────────
  const redFlags = [];
  if (!uploadedSources.includes('OM') && !uploadedSources.includes('T12 / P&L') && !uploadedSources.includes('Rent Roll')) {
    redFlags.push('No financial documents uploaded — all figures are estimated or unavailable');
  }
  if (noiMismatch) redFlags.push(`NOI mismatch: broker states ${fmt(statedNOI)}, formula computes ${fmt(computedNOI)} — difference of ${fmtPct(Math.abs(statedNOI - computedNOI) / Math.max(statedNOI, computedNOI) * 100)}`);
  if (expenseRatio > 0 && expenseRatio < 35) redFlags.push('Expense ratio below 35% — likely missing expense categories');
  if (expenseRatio > 70) redFlags.push('Expense ratio above 70% — NOI critically squeezed');
  else if (expenseRatio > 65) redFlags.push('Expense ratio above 65% — review expense line items');
  if (occupancy > 0 && occupancy < 80) redFlags.push(`Occupancy below 80% (${fmtPct(occupancy)}) — significant vacancy risk`);
  if (displayCapRate > 0 && displayCapRate < 5) redFlags.push(`Sub-5% cap rate (${fmtPct(displayCapRate)}) — thin return margin`);
  if (statedNOI === 0 && totalRevenue === 0) redFlags.push('No NOI or revenue data extracted — financials must be provided for accurate analysis');

  // ── Recommendation ────────────────────────────────────────────────────
  let recommendation = 'Insufficient data';
  let recommendationReason = '';
  let recommendationColor = '#64748b';

  const hasAnyFinancials = totalRevenue > 0 || statedNOI > 0 || displayNOI > 0;

  if (!hasAnyFinancials) {
    recommendation = 'Insufficient data';
    recommendationReason = 'No financial figures could be extracted from the uploaded documents. Upload the OM, Rent Roll, or T12/P&L to get a meaningful Go/No-Go.';
    recommendationColor = '#64748b';
  } else if (redFlags.length >= 3 || (displayNOI <= 0 && statedNOI <= 0)) {
    recommendation = 'Do not proceed';
    recommendationReason = 'Multiple red flags indicate serious financial concerns. The deal economics do not support further investment of time without substantial renegotiation or additional documentation.';
    recommendationColor = '#dc2626';
  } else if (concerns.length >= 3 || expenseRatio > 55 || (occupancy > 0 && occupancy < 85) || noiMismatch) {
    recommendation = 'Proceed with caution';
    recommendationReason = `The deal shows potential but has material concerns that must be resolved before committing. Key issues: ${concerns.slice(0, 2).join('; ') || 'see concerns above'}.`;
    recommendationColor = '#d97706';
  } else if (hasAnyFinancials && positives.length >= 2) {
    recommendation = 'Proceed';
    recommendationReason = `Initial financials look solid. NOI, occupancy, and expense metrics are within acceptable ranges. Move to full underwriting to confirm.`;
    recommendationColor = '#16a34a';
  } else {
    recommendation = 'Proceed with caution';
    recommendationReason = 'Limited data available. What was extracted looks acceptable, but key documents are missing. Proceed to gather more information before making a final call.';
    recommendationColor = '#d97706';
  }

  return {
    snapshot: {
      propertyName: property.propertyName || financials.propertyName || '—',
      location: [property.address || financials.address, property.city || financials.city, property.state || financials.state].filter(Boolean).join(', ') || '—',
      propertyType: property.buildingType || property.assetType || financials.assetType || '—',
      units: units || '—',
      purchasePrice: price || '—',
      revenue: totalRevenue || grossRentAnnual || '—',
      expenses: totalExpenses || opExpenses || '—',
      noi: displayNOI || '—',
      occupancy: occupancy || '—',
      capRate: displayCapRate || capRateStated || '—',
    },
    expenseVerdict,
    expenseSeverity,
    occupancyNote,
    positives,
    concerns,
    redFlags,
    recommendation,
    recommendationReason,
    recommendationColor,
    noiMismatch,
    computedNOI,
    statedNOI,
  };
}

// ─── Upload card (single file slot) ──────────────────────────────────────────
function UploadCard({ doc, file, link, onFile, onRemove, onLink, error, isValidating }) {
  const inputRef = useRef();
  const [drag, setDrag] = useState(false);

  if (doc.id === 'link') {
    return (
      <div className="qda-doc-card" style={{ '--doc-color': doc.color, '--doc-bg': doc.bg }}>
        <div className="qda-doc-icon">{doc.icon}</div>
        <div className="qda-doc-label">{doc.label}</div>
        <div className="qda-doc-sublabel">{doc.sublabel}</div>
        <input
          type="url"
          className="qda-link-input"
          placeholder="https://loopnet.com/..."
          value={link}
          onChange={e => {
            onLink(e.target.value);
            // persist link immediately so it survives navigation
            try {
              const session = loadQdaSession() || {};
              saveQdaSession({ ...session, listingLink: e.target.value });
            } catch { }
          }}
        />
        {link && (
          <div className="qda-doc-hint qda-doc-hint--has">✓ URL added</div>
        )}
      </div>
    );
  }

  const cardClass = [
    'qda-doc-card',
    drag ? 'drag-over' : '',
    file ? 'has-file' : '',
    error ? 'has-error' : '',
    isValidating ? 'is-validating' : '',
  ].filter(Boolean).join(' ');

  return (
    <div
      className={cardClass}
      style={{ '--doc-color': doc.color, '--doc-bg': doc.bg }}
      onClick={() => !file && !isValidating && inputRef.current?.click()}
      onDragOver={e => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={e => {
        e.preventDefault(); setDrag(false);
        const f = e.dataTransfer.files[0];
        if (f && !isValidating) onFile(f);
      }}
    >
      <input
        ref={inputRef}
        type="file"
        accept={doc.accept}
        style={{ display: 'none' }}
        onChange={e => {
          if (e.target.files[0]) {
            onFile(e.target.files[0]);
            e.target.value = '';
          }
        }}
      />
      <div className="qda-doc-icon">
        {isValidating ? '⏳' : file ? (file.isRestored ? '📋' : '') : error ? '❌' : doc.icon}
      </div>
      <div className="qda-doc-label">{doc.label}</div>
      <div className="qda-doc-sublabel">
        {isValidating
          ? 'Checking…'
          : file
            ? (file.name.length > 20 ? file.name.slice(0, 18) + '…' : file.name)
            : doc.sublabel}
      </div>
      {file?.isRestored && (
        <div style={{ fontSize: 9, color: '#6366f1', fontWeight: 600, marginTop: 2, letterSpacing: 0.3 }}>
          RESTORED
        </div>
      )}
      {file ? (
        <button
          className="qda-doc-remove"
          onClick={e => { e.stopPropagation(); onRemove(); }}
          title={`Remove ${doc.label}`}
        >×</button>
      ) : (
        !isValidating && <div className="qda-doc-hint">{error ? 'Try again' : 'Drop or click'}</div>
      )}
      {error && (
        <div className="qda-doc-validation-error">{error}</div>
      )}
    </div>
  );
}

// ─── Recommendation badge ─────────────────────────────────────────────────────
function RecoBadge({ recommendation, color }) {
  const icon = {
    'Proceed': '',
    'Proceed with caution': '⚠️',
    'Do not proceed': '🚫',
    'Insufficient data': '❓',
  }[recommendation] || '❓';

  return (
    <div className="qda-reco-badge" style={{ '--reco-color': color }}>
      <span className="qda-reco-icon">{icon}</span>
      <span className="qda-reco-label">{recommendation}</span>
    </div>
  );
}

// ─── Main page component ──────────────────────────────────────────────────────
function QuickDealAnalysisPage() {
  // ── Restore persisted session on mount ──────────────────────────────────────
  // File objects (binary) can't be stored, so we restore them as
  // { name, isRestored: true } — the UI shows the filename, and Re-Run
  // uses the saved parsedData instead of re-parsing.
  const [files, setFiles] = useState(() => {
    const s = loadQdaSession();
    const fn = s?.fileNames || {};
    return {
      om: fn.om ? { name: fn.om, isRestored: true } : null,
      rentRoll: fn.rentRoll ? { name: fn.rentRoll, isRestored: true } : null,
      t12: fn.t12 ? { name: fn.t12, isRestored: true } : null,
      photos: fn.photos ? { name: fn.photos, isRestored: true } : null,
    };
  });
  const [docValidationErrors, setDocValidationErrors] = useState({ om: null, rentRoll: null, t12: null });
  const [validatingId, setValidatingId] = useState(null);
  const [docErrorPopup, setDocErrorPopup] = useState(null); // { label, message }
  const [listingLink, setListingLink] = useState(() => loadQdaSession()?.listingLink || '');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(() => loadQdaSession()?.analysisResult || null);
  const [parseProgress, setParseProgress] = useState({});
  // Extraction log drawer
  const [logOpen, setLogOpen] = useState(false);
  // Per-doc extracted field snapshot (for the log drawer)
  const [docFields, setDocFields] = useState({});
  // Cached parsed data from the last successful run (used when files are restored)
  const cachedParsedData = useRef(loadQdaSession()?.parsedData || null);

  const hasAnyInput = files.om || files.rentRoll || files.t12 || files.photos || listingLink.trim();

  const setFile = useCallback(async (id, file) => {
    // Clear previous error for this slot
    setDocValidationErrors(prev => ({ ...prev, [id]: null }));
    // Clear result when a new file is added (stale results no longer apply)
    setAnalysisResult(null);
    clearQdaSession();
    cachedParsedData.current = null;

    if (!file || id === 'photos') {
      setFiles(prev => ({ ...prev, [id]: file }));
      return;
    }

    // For document types that need validation, validate before accepting
    if (id === 'om' || id === 'rentRoll' || id === 't12') {
      setValidatingId(id);
      try {
        const parsed = await parseDealDocument(file, id);
        if (parsed?.validationResult && !parsed.validationResult.isValid) {
          const docLabel = DOC_TYPES.find(d => d.id === id)?.label || id;
          setDocErrorPopup({ label: docLabel, message: parsed.validationResult.message });
          setDocValidationErrors(prev => ({ ...prev, [id]: parsed.validationResult.message }));
          setValidatingId(null);
          return; // reject the file — don't add to state
        }
      } catch {
        // on parse error, allow the file through (validation is best-effort)
      }
      setValidatingId(null);
    }

    setFiles(prev => ({ ...prev, [id]: file }));
  }, []);

  const removeFile = useCallback((id) => {
    setFiles(prev => ({ ...prev, [id]: null }));
    setDocValidationErrors(prev => ({ ...prev, [id]: null }));
    setDocErrorPopup(null);
    setAnalysisResult(null);
    clearQdaSession();
    cachedParsedData.current = null;
  }, []);

  // ── Run Analysis ────────────────────────────────────────────────────────────
  const handleRunAnalysis = async () => {
    setIsAnalyzing(true);
    setAnalysisResult(null);

    // Track which sources are being used
    const uploadedSources = [];
    if (files.om) uploadedSources.push('OM');
    if (files.rentRoll) uploadedSources.push('Rent Roll');
    if (files.t12) uploadedSources.push('T12 / P&L');
    if (files.photos) uploadedSources.push('Photos');
    if (listingLink.trim()) uploadedSources.push('Listing URL');

    // Check if all files are restored (no real binary) — reuse cached parsed data
    const allRestored = [files.om, files.rentRoll, files.t12]
      .filter(Boolean)
      .every(f => f.isRestored);

    let merged;

    if (allRestored && cachedParsedData.current) {
      // Files are restored from a previous session — use the saved parsed data
      merged = cachedParsedData.current;
      setParseProgress({
        ...(files.om ? { om: 'done' } : {}),
        ...(files.rentRoll ? { rentRoll: 'done' } : {}),
        ...(files.t12 ? { t12: 'done' } : {}),
      });
    } else {
      // Real File objects present — parse them fresh
      merged = { financials: {}, property: {}, rentRoll: {} };

      const parseDoc = async (file, type) => {
        if (!file || file.isRestored) return;
        try {
          setParseProgress(p => ({ ...p, [type]: 'parsing' }));
          const result = await parseDealDocument(file, type);
          if (result?.fields) {
            const f = result.fields;

            // USA CRE data-source priority:
            //   T12  = trailing 12-month actuals → ALWAYS wins on financials
            //   RR   = current rent roll         → ALWAYS wins on rent & occupancy
            //   OM   = broker figures            → wins on deal info, fills empty slots
            // Documents are parsed sequentially (OM → T12 → RR) so later sources
            // correctly override earlier ones for their priority fields.
            const T12_FIELDS  = ['grossRent','opExpenses','noi','otherIncome','vacancyRate','insurance']
            const RR_FIELDS   = ['grossRent','vacancyRate']
            const OM_INFO     = ['askingPrice','capRate','propertyName','address','city','state',
                                 'zip','units','assetType','occupancy','yearBuilt',
                                 'brokerName','brokerPhone','brokerEmail']

            const fin = { ...merged.financials }
            if (type === 'om') {
              // OM: set all fields (baseline), only fill empty for financials
              OM_INFO.forEach(k => { if (f[k]) fin[k] = f[k] })
              T12_FIELDS.forEach(k => { if (f[k] && !fin[k]) fin[k] = f[k] })
            } else if (type === 't12') {
              // T12: always override financial fields (actuals > pro-forma)
              T12_FIELDS.forEach(k => { if (f[k]) fin[k] = f[k] })
            } else if (type === 'rentRoll') {
              // RR: always override rent & occupancy
              RR_FIELDS.forEach(k => { if (f[k]) fin[k] = f[k] })
              if (f.units && !merged.property.units) fin.units = f.units
            }
            merged.financials = fin

            merged.property = {
              ...merged.property,
              propertyName: f.propertyName || merged.property.propertyName,
              address:      f.address      || merged.property.address,
              city:         f.city         || merged.property.city,
              state:        f.state        || merged.property.state,
              zip:          f.zip          || merged.property.zip,
              units:        f.units        || merged.property.units,
              buildingType: f.buildingType || f.assetType || merged.property.buildingType,
              askingPrice:  f.askingPrice  || merged.property.askingPrice,
              occupancy:    f.occupancy    || merged.property.occupancy,
              yearBuilt:    f.yearBuilt    || merged.property.yearBuilt,
            }

            if (type === 'rentRoll') {
              merged.rentRoll = {
                ...merged.rentRoll,
                occupancyRate: f.occupancy
                  ? Number(f.occupancy)
                  : (f.vacancyRate ? Math.max(0, 100 - Number(f.vacancyRate)) : merged.rentRoll.occupancyRate),
                averageRent: f.grossRent && f.units
                  ? Math.round(Number(f.grossRent) / Number(f.units))
                  : merged.rentRoll.averageRent,
              }
            }

            setDocFields(prev => ({ ...prev, [type]: result.fields }))
          }
          setParseProgress(p => ({ ...p, [type]: 'done' }))
        } catch {
          setParseProgress(p => ({ ...p, [type]: 'error' }))
        }
      }

      // Parse sequentially: OM first (baseline), then T12 (overrides financials),
      // then RR (overrides rent & occupancy). Sequential ensures correct priority.
      await parseDoc(files.om, 'om')
      await parseDoc(files.t12, 't12')
      await parseDoc(files.rentRoll, 'rentRoll')

      // If we parsed any real files AND had some restored ones, merge cached data in
      if (cachedParsedData.current) {
        merged = {
          financials: { ...cachedParsedData.current.financials, ...merged.financials },
          property: { ...cachedParsedData.current.property, ...merged.property },
          rentRoll: { ...cachedParsedData.current.rentRoll, ...merged.rentRoll },
        };
      }
    }

    // Run the analysis engine
    const result = runAnalysis({ parsed: merged, listingLink, uploadedSources });
    result.uploadedSources = uploadedSources;

    // Small delay for UX
    await new Promise(r => setTimeout(r, 600));

    // ── Persist session so it survives navigation ─────────────────────────────
    cachedParsedData.current = merged;
    saveQdaSession({
      analysisResult: result,
      fileNames: {
        om: files.om?.name || null,
        rentRoll: files.rentRoll?.name || null,
        t12: files.t12?.name || null,
        photos: files.photos?.name || null,
      },
      parsedData: merged,
      listingLink,
    });

    setAnalysisResult(result);
    setIsAnalyzing(false);
  };

  // ── Empty state ─────────────────────────────────────────────────────────────
  const renderEmptyState = () => (
    <div className="qda-empty">
      <div className="qda-empty-icon">📂</div>
      <p className="qda-empty-text">
        Upload any available deal document to start Quick Deal Analysis.
        More files will improve accuracy.
      </p>
    </div>
  );

  // ── Analysis result panel ───────────────────────────────────────────────────
  const renderAnalysis = () => {
    if (!analysisResult) return null;
    const r = analysisResult;
    const s = r.snapshot;

    return (
      <div className="qda-result">
        {/* Title */}
        <div className="qda-result-title">
          <span className="qda-result-title-icon">⚡</span>
          Quick Deal Analysis
        </div>

        {/* Available data */}
        <div className="qda-result-section">
          <div className="qda-result-section-label">Available Data</div>
          <div className="qda-sources-list">
            {r.uploadedSources.length > 0
              ? r.uploadedSources.map(src => (
                <span key={src} className="qda-source-chip">{src}</span>
              ))
              : <span className="qda-source-chip qda-source-chip--none">No documents uploaded</span>
            }
          </div>
        </div>

        {/* Deal Snapshot */}
        <div className="qda-result-section">
          <div className="qda-result-section-label">Deal Snapshot</div>
          <div className="qda-snapshot-grid">
            {[
              { label: 'Property Name', value: s.propertyName },
              { label: 'Location', value: s.location },
              { label: 'Property Type', value: s.propertyType },
              { label: 'Unit Count', value: s.units !== '—' ? `${s.units} units` : '—' },
              { label: 'Purchase Price', value: s.purchasePrice !== '—' ? fmt(s.purchasePrice) : '—' },
              { label: 'Revenue', value: s.revenue !== '—' ? fmt(s.revenue) : '—' },
              { label: 'Expenses', value: s.expenses !== '—' ? fmt(s.expenses) : '—' },
              { label: 'NOI', value: s.noi !== '—' ? fmt(s.noi) : '—' },
              { label: 'Occupancy', value: s.occupancy !== '—' ? fmtPct(s.occupancy) : '—' },
              { label: 'Cap Rate', value: s.capRate !== '—' ? fmtPct(s.capRate) : '—' },
            ].map(({ label, value }) => (
              <div key={label} className="qda-snapshot-row">
                <span className="qda-snapshot-label">{label}</span>
                <span className={`qda-snapshot-value ${value === '—' ? 'qda-snapshot-value--empty' : ''}`}>{value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* NOI mismatch alert */}
        {r.noiMismatch && (
          <div className="qda-alert qda-alert--warning">
            <span>⚠️</span>
            <div>
              <strong>NOI Mismatch Detected</strong>
              <p>Broker states {fmt(r.statedNOI)} — formula computes {fmt(r.computedNOI)} from the uploaded data. Verify all income and expense line items before proceeding.</p>
            </div>
          </div>
        )}

        {/* Expense & NOI Review */}
        <div className="qda-result-section">
          <div className="qda-result-section-label">Expense &amp; NOI Review</div>
          <div className={`qda-expense-verdict qda-expense-verdict--${r.expenseSeverity}`}>
            {r.expenseVerdict}
          </div>
          {r.occupancyNote && (
            <div className="qda-occupancy-note">{r.occupancyNote}</div>
          )}
        </div>

        {/* Key Findings */}
        <div className="qda-result-section">
          <div className="qda-result-section-label">Key Findings</div>
          <div className="qda-findings">
            {r.positives.length > 0 && (
              <div className="qda-findings-col">
                <div className="qda-findings-col-label qda-findings-col-label--pos">Positives</div>
                {r.positives.map((f, i) => (
                  <div key={i} className="qda-finding-item qda-finding-item--pos">
                    <span>✓</span> {f}
                  </div>
                ))}
              </div>
            )}
            {r.concerns.length > 0 && (
              <div className="qda-findings-col">
                <div className="qda-findings-col-label qda-findings-col-label--concern">Concerns</div>
                {r.concerns.map((f, i) => (
                  <div key={i} className="qda-finding-item qda-finding-item--concern">
                    <span>!</span> {f}
                  </div>
                ))}
              </div>
            )}
            {r.positives.length === 0 && r.concerns.length === 0 && (
              <div className="qda-findings-empty">Upload financial documents to generate findings.</div>
            )}
          </div>
        </div>

        {/* Red Flags */}
        {r.redFlags.length > 0 && (
          <div className="qda-result-section">
            <div className="qda-result-section-label">Red Flags</div>
            <div className="qda-redflags">
              {r.redFlags.map((flag, i) => (
                <div key={i} className="qda-redflag-item">
                  <span className="qda-redflag-icon">🚩</span>
                  <span>{flag}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recommendation */}
        <div className="qda-result-section qda-result-section--reco">
          <div className="qda-result-section-label">Recommendation</div>
          <RecoBadge recommendation={r.recommendation} color={r.recommendationColor} />
          <div className="qda-reco-reason">
            <strong>Reason:</strong> {r.recommendationReason}
          </div>
        </div>

        {/* Reset */}
        <button
          className="qda-reset-btn"
          onClick={() => {
            setAnalysisResult(null);
            setParseProgress({});
            setFiles({ om: null, rentRoll: null, t12: null, photos: null });
            setListingLink('');
            clearQdaSession();
            cachedParsedData.current = null;
          }}
        >
          ↩ Start New Analysis
        </button>
      </div>
    );
  };

  return (
    <div className="qda-page">

      {/* ── Wrong-document rejection popup ──────────────────────────────────── */}
      {docErrorPopup && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: 'rgba(0,0,0,0.55)', display: 'flex',
          alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{
            background: '#fff', borderRadius: 14, padding: '36px 36px 28px',
            maxWidth: 460, width: '92%',
            boxShadow: '0 16px 48px rgba(0,0,0,0.25)',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: 48, marginBottom: 12, lineHeight: 1 }}>❌</div>
            <h3 style={{ margin: '0 0 6px', fontSize: 19, color: '#dc2626', fontWeight: 700 }}>
              Wrong Document Uploaded
            </h3>
            <p style={{ margin: '0 0 16px', color: '#0f172a', fontSize: 15, fontWeight: 600 }}>
              {docErrorPopup.label} slot rejected this file.
            </p>
            <p style={{
              margin: '0 0 24px', color: '#64748b', fontSize: 14,
              background: '#fef2f2', border: '1px solid #fecaca',
              borderRadius: 8, padding: '12px 16px', lineHeight: 1.6,
              textAlign: 'left',
            }}>
              {docErrorPopup.message}
            </p>
            <button
              onClick={() => setDocErrorPopup(null)}
              style={{
                padding: '11px 36px', borderRadius: 8, border: 'none',
                background: '#dc2626', color: '#fff', fontWeight: 700,
                cursor: 'pointer', fontSize: 14, letterSpacing: 0.2,
              }}
            >
              OK — Upload the Correct Document
            </button>
          </div>
        </div>
      )}

      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div className="qda-header">
        <div className="qda-header-left">
          <span className="qda-badge">Quick Deal Analysis</span>
          <div>
            <p className="qda-subtitle">
              Upload available deal documents and get a fast analysis of expenses, NOI, risks,
              and whether the deal is worth pursuing.
            </p>
          </div>
        </div>
        <div className="qda-header-badge">
          <span className="qda-header-step">Pre-Pipeline</span>
        </div>
      </div>

      {/* ── Body ────────────────────────────────────────────────────────────── */}
      <div className="qda-body">
        {/* Left: Upload panel */}
        <div className="qda-upload-panel">
          <div className="qda-panel-header">
            <span className="qda-panel-icon">📂</span>
            <div>
              <h2>Upload Deal Documents</h2>
              <p className="qda-panel-desc">
                Upload any combination of documents below.
              </p>
            </div>
          </div>

          {/* Document grid */}
          <div className="qda-doc-grid">
            {DOC_TYPES.map(doc => (
              <UploadCard
                key={doc.id}
                doc={doc}
                file={doc.id !== 'link' ? files[doc.id] : null}
                link={doc.id === 'link' ? listingLink : ''}
                onFile={file => setFile(doc.id, file)}
                onRemove={() => removeFile(doc.id)}
                onLink={setListingLink}
                error={docValidationErrors[doc.id] || null}
                isValidating={validatingId === doc.id}
              />
            ))}
          </div>

          {/* Upload summary */}
          {hasAnyInput && (
            <div className="qda-upload-summary">
              {[
                files.om && '📄 OM',
                files.rentRoll && '📊 Rent Roll',
                files.t12 && '📈 T12 / P&L',
                files.photos && '🖼️ Photos',
                listingLink.trim() && '🔗 Link',
              ].filter(Boolean).join(' · ')} uploaded
            </div>
          )}

          {/* Parse progress */}
          {isAnalyzing && (
            <div className="qda-parse-progress">
              {Object.entries(parseProgress).map(([key, status]) => (
                <div key={key} className={`qda-parse-item qda-parse-item--${status}`}>
                  <span className="qda-parse-spinner" />
                  <span>{key === 'om' ? 'OM' : key === 'rentRoll' ? 'Rent Roll' : 'T12 / P&L'}</span>
                  <span className="qda-parse-status">
                    {status === 'parsing' ? 'Parsing…' : status === 'done' ? '✓ Done' : '⚠ Error'}
                  </span>
                </div>
              ))}
              <div className="qda-analyzing-text">
                <span className="qda-analyze-spinner" />
                Running financial analysis…
              </div>
            </div>
          )}

          {/* CTA Button */}
          {!isAnalyzing && (
            <button
              className={`qda-run-btn ${!hasAnyInput ? 'qda-run-btn--disabled' : ''}`}
              onClick={hasAnyInput ? handleRunAnalysis : undefined}
              disabled={!hasAnyInput}
              title={!hasAnyInput ? 'Upload at least one document or add a listing URL to run analysis' : ''}
            >
              {analysisResult ? '↻ Re-Run Quick Deal Analysis' : '⚡ Run Quick Deal Analysis'}
            </button>
          )}

          {/* Empty state message */}
          {!hasAnyInput && !analysisResult && renderEmptyState()}
        </div>

        {/* Right: Result panel */}
        <div className="qda-result-panel">
          {!analysisResult && !isAnalyzing && (
            <div className="qda-result-placeholder">
              <div className="qda-placeholder-icon">📋</div>
              <h3>Analysis will appear here</h3>
              <p>Upload your deal documents on the left and click <strong>Run Quick Deal Analysis</strong> to get your Go / No-Go in no time.</p>
              <div className="qda-placeholder-checklist">
                <div className={files.om ? 'qda-check done' : 'qda-check'}>
                  <span>{files.om ? '' : '○'}</span> OM — property details, broker assumptions
                </div>
                <div className={files.rentRoll ? 'qda-check done' : 'qda-check'}>
                  <span>{files.rentRoll ? '' : '○'}</span> Rent Roll — unit income, occupancy, lease risks
                </div>
                <div className={files.t12 ? 'qda-check done' : 'qda-check'}>
                  <span>{files.t12 ? '' : '○'}</span> T12 / P&L — actual revenue, expense trends
                </div>
                <div className={files.photos ? 'qda-check done' : 'qda-check'}>
                  <span>{files.photos ? '' : '○'}</span> Photos — property condition
                </div>
                <div className={listingLink.trim() ? 'qda-check done' : 'qda-check'}>
                  <span>{listingLink.trim() ? '' : '○'}</span> Listing URL — market positioning
                </div>
              </div>
            </div>
          )}
          {isAnalyzing && (
            <div className="qda-analyzing">
              <div className="qda-analyzing-spinner" />
              <h3>Analyzing your deal…</h3>
              <p>Parsing documents and running financial analysis. This takes about 10–15 seconds.</p>
            </div>
          )}
          {analysisResult && renderAnalysis()}
        </div>
      </div>

      {/* ── Extraction + Calculation Log Drawer ──────────────────────────── */}
      {(analysisResult || Object.keys(parseProgress).length > 0) && !logOpen && (
        <button className="elog-fab" onClick={() => setLogOpen(true)}>
          Analysis Log
        </button>
      )}
      {(analysisResult || Object.keys(parseProgress).length > 0) && (
        <aside className={`elog-drawer${logOpen ? ' open' : ''}`} aria-label="Extraction and calculation log">
          <div className="elog-header">
            <div className="elog-header-text">
              <span>PDF to MVP Review</span>
              <strong>Extracted fields and calculated outputs</strong>
            </div>
            <button className="elog-close" onClick={() => setLogOpen(false)} aria-label="Close log">×</button>
          </div>

          {/* Extraction Steps */}
          <div className="elog-section">
            <h3>Extraction Steps</h3>
            {['om', 'rentRoll', 't12'].filter(id => files[id]).map(id => {
              const label = DOC_TYPES.find(d => d.id === id)?.label || id;
              const state = parseProgress[id] || (files[id]?.isRestored ? 'done' : 'loading');
              const steps = {
                om:       ['Upload received', 'Text extraction complete', 'Fields mapped into MVP', 'SDA formulas recalculated'],
                rentRoll: ['Upload received', 'Rent roll parsed', 'Occupancy & rent extracted'],
                t12:      ['Upload received', 'T12 financials parsed', 'Revenue & expenses extracted'],
              }[id] || [];
              return (
                <div key={id} className={`elog-doc ${state === 'parsing' ? 'loading' : state}`}>
                  <div className="elog-doc-head">
                    <strong>{label}</strong>
                    <span>{files[id]?.name || ''}</span>
                  </div>
                  <p>{state === 'parsing' ? 'Parsing document…' : state === 'done' ? 'Document read complete.' : state === 'error' ? 'Parser failed.' : 'Waiting…'}</p>
                  {state === 'done' && <ol>{steps.map(s => <li key={s}>{s}</li>)}</ol>}
                </div>
              );
            })}
            {['om', 'rentRoll', 't12'].every(id => !files[id]) && (
              <p className="elog-muted">No documents uploaded yet.</p>
            )}
          </div>

          {/* Just Extracted — per-doc fields */}
          <div className="elog-section">
            <h3>Just Extracted</h3>
            {Object.keys(docFields).length > 0 ? (
              <div className="elog-field-list">
                {Object.entries(docFields).flatMap(([docId, fields]) => {
                  const docLabel = DOC_TYPES.find(d => d.id === docId)?.label || docId;
                  const LABELS = {
                    propertyName: 'Property Name', address: 'Address', city: 'City', state: 'State',
                    zip: 'ZIP', units: 'Units', assetType: 'Asset Type', askingPrice: 'Asking Price',
                    occupancy: 'Occupancy', capRate: 'Cap Rate', grossRent: 'Monthly Gross Rent',
                    otherIncome: 'Other Income / Mo', vacancyRate: 'Vacancy Rate',
                    opExpenses: 'Annual Op. Expenses', noi: 'NOI',
                    brokerName: 'Broker Name', brokerPhone: 'Broker Phone', brokerEmail: 'Broker Email',
                    extractionProfile: 'Extraction Profile',
                  };
                  return Object.entries(fields)
                    .filter(([, v]) => v !== '' && v != null)
                    .map(([key, val]) => (
                      <div key={`${docId}-${key}`}>
                        <span>{LABELS[key] || key}<small>{docLabel}</small></span>
                        <b>{String(val)}</b>
                      </div>
                    ));
                })}
              </div>
            ) : analysisResult ? (
              <div className="elog-field-list">
                {/* Show merged snapshot when re-running from cached data */}
                {Object.entries({
                  'Property':     analysisResult.snapshot?.propertyName,
                  'Location':     analysisResult.snapshot?.location,
                  'Units':        analysisResult.snapshot?.units,
                  'Asking Price': analysisResult.snapshot?.purchasePrice ? fmt(analysisResult.snapshot.purchasePrice) : null,
                  'Occupancy':    analysisResult.snapshot?.occupancy ? fmtPct(analysisResult.snapshot.occupancy) : null,
                  'Cap Rate':     analysisResult.snapshot?.capRate ? fmtPct(analysisResult.snapshot.capRate) : null,
                }).filter(([, v]) => v && v !== '—').map(([label, val]) => (
                  <div key={label}>
                    <span>{label}<small>merged sources</small></span>
                    <b>{String(val)}</b>
                  </div>
                ))}
              </div>
            ) : (
              <p className="elog-muted">Run analysis to see extracted fields.</p>
            )}
          </div>

          {/* Being Calculated — SDA formula outputs */}
          {analysisResult && (
            <div className="elog-section">
              <h3>Being Calculated</h3>
              {analysisResult.noiMismatch && (
                <div className="elog-warning">
                  ⚠ NOI mismatch: broker states {fmt(analysisResult.statedNOI)}, formula computes {fmt(analysisResult.computedNOI)}. Cross-check required.
                </div>
              )}
              <div className="elog-field-list calculated">
                {[
                  ['Total Revenue',    analysisResult.snapshot?.revenue,  'gross rent + other income', 'grossRent × 12 + otherIncome × 12'],
                  ['Total Expenses',   analysisResult.snapshot?.expenses, 'from OM / T12 operating expenses', 'opExpenses (annual)'],
                  ['NOI',              analysisResult.snapshot?.noi,      'revenue − expenses (or stated NOI)', 'revenue − opExpenses'],
                  ['Cap Rate',         analysisResult.snapshot?.capRate,  'NOI ÷ purchase price', 'NOI / askingPrice × 100'],
                  ['Expense Ratio',    null,                              analysisResult.expenseVerdict, null],
                  ['Occupancy Note',   null,                              analysisResult.occupancyNote, null],
                  ['Recommendation',   analysisResult.recommendation,    analysisResult.recommendationReason, null],
                ].map(([label, numVal, hint, formula]) => (
                  <div key={label}>
                    <span>
                      {label}
                      <small>{hint}</small>
                      {formula && <p className="elog-formula">{formula}</p>}
                    </span>
                    <b>{numVal != null && numVal !== '—' ? (typeof numVal === 'number' ? fmt(numVal) : String(numVal)) : ''}</b>
                  </div>
                ))}
              </div>
            </div>
          )}
        </aside>
      )}

    </div>
  );
}

export default QuickDealAnalysisPage;
